// @flow

import { Link } from '@reach/router';

import Button from 'antd/lib/button';
import notification from 'antd/lib/notification';
import Popconfirm from 'antd/lib/popconfirm';
import isEmpty from 'lodash/isEmpty';
import qs from 'query-string';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  ListSection,
  PageListSection
} from '../../../components/layout/Section';
import { TableHeader } from '../../../components/ui/Table';

import type { PersistFilterPayload } from '../../../ducks/persistFilters';
import { setFilter } from '../../../ducks/persistFilters';
import type { AppState } from '../../../ducks/redux';

import { ACT_COLUMNS, canHandleService } from '../lib';
import Header from './../../../components/layout/Header';
import { notificationLoading } from './../../../components/Notifications';
import { ButtonsRow, Icon, Table } from './../../../components/ui';
import {
  expertiseServiceGpmApi,
  type FetchListParams
} from './../../../lib/api';
import { serviceTypes } from './../../../lib/enum/serviceTypes';
import {
  checkDate,
  convertVehicleToString,
  formatDateTimeToString,
  getListInitialState,
  navigate,
  setQueryParams,
  toLocalStringRu
} from './../../../lib/helpers';

import type {
  ExpertiseServiceGpm,
  ListState,
  UserAccess,
  Vehicle
} from './../../../lib/types';
import { withUserAccess } from './../../withUserAccess';

import type { ExpertisesServicesGpmFilterParams } from './Filter';
import ExpertisesServicesGpmFilter from './Filter';

type Props = {
  location: Location & { state: { page: number } },
  userAccess: UserAccess[],
  persistFilters: any,
  setFilter: (payload: PersistFilterPayload) => void
};
type State = ListState<ExpertiseServiceGpm> & {
  filterPath: string
};

export const COLUMNS = [
  {
    title: 'Марка',
    sorter: true,
    dataIndex: 'vehicle',
    width: 300,
    breakByWidth: true,
    render: (vehicle: Vehicle) => convertVehicleToString(vehicle)
  },
  {
    title: 'Рег. номер ГПМ',
    dataIndex: 'vehicle.gpmRegistrationNumber',
    width: 150
  },
  {
    title: <TableHeader width="100px">Дата экспертизы/ТО</TableHeader>,
    dataIndex: 'maintenanceDate',
    width: 100,
    render: (value: string) => checkDate(value)
  },
  {
    title: <TableHeader width="100px">Дата след. экспертизы/ТО</TableHeader>,
    dataIndex: 'nextActDate',
    width: 100,
    render: (value: string) => formatDateTimeToString(value, 'DD.MM.YYYY')
  },
  {
    title: 'Вид услуги',
    dataIndex: 'services',
    width: 200,
    breakByWidth: true,
    render: (value: string) => {
      return value ? serviceTypes[value] : null;
    }
  },
  {
    title: <TableHeader width="100px">Сумма оплаты в руб. без НДС</TableHeader>,
    dataIndex: 'paymentAmount',
    width: 100,
    render: (paymentAmount: number) => toLocalStringRu(paymentAmount || 0)
  }
];

export class List extends Component<Props, State> {
  static defaultProps = {
    location: {}
  };
  state = {
    ...getListInitialState(),
    filterPath: window.location.pathname
  };
  columns = [...COLUMNS, ...ACT_COLUMNS];

  async componentDidMount() {
    const { page, ...filter } = qs.parse(window.location.search);
    if (!isEmpty(filter)) {
      await this.setPersistFilter(filter);
    }
    if (this.canAdd()) {
      this.columns.push({
        stopPropagation: true,
        width: 20,
        dataIndex: 'id',
        render: (id: number) => (
          <Popconfirm
            title="Вы уверены, что хотите удалить запись?"
            onConfirm={async () => this.deleteExpertiseServiceGpm(id)}
          >
            <Icon type="x" />
          </Popconfirm>
        )
      });
    }
    await this.fetchExpertiseServiceGpmList(page);
  }

  deleteExpertiseServiceGpm = async (id: number) => {
    try {
      notificationLoading({
        message: 'Удаление...',
        key: 'deleting'
      });
      await expertiseServiceGpmApi.deleteExpertiseServiceGpm(id);
      await this.fetchExpertiseServiceGpmList(this.state.page);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    } finally {
      notification.close('deleting');
    }
  };

  fetchExpertiseServiceGpmList = async (
    page: number = 1,
    params: FetchListParams<> = {}
  ) => {
    const { filterPath } = this.state;
    const filter = this.props.persistFilters[filterPath] || {};
    try {
      this.setState({ loading: true });
      const {
        data,
        totalCount
      } = await expertiseServiceGpmApi.fetchExpertiseServiceGpmList({
        ...filter,
        page,
        ...params
      });
      setQueryParams({ page });
      this.setState({ data, totalCount, page });
    } catch (err) {
      notification.error({
        message: 'Ошибка',
        description: err.message
      });
    } finally {
      this.setState({
        loading: false
      });
    }
  };

  handleRowClick = (id: number) =>
    navigate(`/services/expertises-services-gpm/${id}`);

  canAdd = () => canHandleService(this.props.userAccess);

  setPersistFilter = async (values: ExpertisesServicesGpmFilterParams) => {
    const { filterPath } = this.state;
    await this.props.setFilter({
      path: filterPath,
      values
    });
  };

  applyFilter = async (filter: ExpertisesServicesGpmFilterParams = {}) => {
    await this.setPersistFilter(filter);
    await this.fetchExpertiseServiceGpmList();
  };

  handlePrint = async () => {
    try {
      const filter = this.props.persistFilters[this.state.filterPath];
      notificationLoading({
        message: 'Загрузка файла отчета...',
        key: 'print'
      });
      await expertiseServiceGpmApi.printReport(filter);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    } finally {
      notification.close('print');
    }
  };

  cleanFilter = this.applyFilter;

  render() {
    const { location } = this.props;
    const {
      data,
      totalCount,
      pageSize,
      page,
      loading,
      filterPath
    } = this.state;
    const filter = this.props.persistFilters[filterPath];
    return (
      <PageListSection>
        <Header
          left={<h1>Экспертиза ПС/ТО приборов безопасности ПС</h1>}
          right={
            <ButtonsRow>
              <Button onClick={this.handlePrint}>Печать</Button>
              {this.canAdd() && (
                <Link to="/services/expertises-services-gpm/new">
                  <Button type="primary" data-cy="add">
                    Создать
                  </Button>
                </Link>
              )}
            </ButtonsRow>
          }
        />
        <ListSection>
          <ExpertisesServicesGpmFilter
            filter={filter}
            applyFilter={this.applyFilter}
            cleanFilter={this.cleanFilter}
          />
          <Table
            onRow={record => ({
              onClick: () => this.handleRowClick(record.id)
            })}
            fetch={this.fetchExpertiseServiceGpmList}
            pagination={{
              page,
              pageSize,
              totalCount,
              location
            }}
            loading={loading}
            columns={this.columns}
            data={data.map(item => ({
              ...item,
              key: item.id
            }))}
            scroll={{
              x: 'auto',
              y: 'calc(100vh - 280px)'
            }}
          />
        </ListSection>
      </PageListSection>
    );
  }
}

export default connect(
  (state: AppState) => ({
    persistFilters: state.persistFilters
  }),
  {
    setFilter
  }
)(withUserAccess(List));
